import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { userService, adminService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import CreateUserModal from '../components/CreateUserModal';
import EditUserModal from '../components/EditUserModal';
import PasswordChangeModal from '../components/PasswordChangeModal';
import SendEmailModal from '../components/SendEmailModal';

const UserManagement = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [pending, setPending] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const isAdmin = currentUser?.role === 'administrator';

  useEffect(() => {
    fetchUsers();
    if (isAdmin) {
      fetchOrganizations();
      fetchPending();
    }
  }, [selectedOrg]);

  // Altes fetes des de la pantalla d'entrada, que entren com a analistes i
  // inactives. Fins que algú les activi, aquesta gent no pot entrar.
  const fetchPending = async () => {
    try {
      setPending(await userService.getPendingUsers());
    } catch (error) {
      console.error('Error fetching pending users:', error);
    }
  };

  const handleApprove = async (user) => {
    setBusyId(user.id);
    try {
      await userService.approveUser(user.id);
      toast.success(t('approvals.approved', { name: user.full_name || user.username }));
      await Promise.all([fetchPending(), fetchUsers()]);
    } catch (error) {
      toast.error(t('approvals.approveError'));
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (user) => {
    if (!window.confirm(t('approvals.rejectConfirm', { name: user.full_name || user.username }))) {
      return;
    }
    setBusyId(user.id);
    try {
      await userService.rejectUser(user.id);
      toast.success(t('approvals.rejected'));
      await Promise.all([fetchPending(), fetchUsers()]);
    } catch (error) {
      toast.error(t('approvals.rejectError'));
    } finally {
      setBusyId(null);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedOrg) {
        params.organization = selectedOrg;
      }
      const data = await userService.getUsers(params);
      setUsers(data);
    } catch (error) {
      toast.error(t('userManagement.fetchError'));
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const data = await userService.getOrganizations();
      setOrganizations(data);
    } catch (error) {
      console.error('Error fetching organizations:', error);
    }
  };

  const handleCreateUser = async (userData) => {
    try {
      const newUser = await userService.createUser(userData);
      setUsers(prev => [...prev, newUser]);
      setShowCreateModal(false);
      
      // Aquesta confirmació es queda, i amb temps de sobra: quan l'usuari es
      // crea sense contrasenya, el servidor en genera una i aquest avís és
      // l'ÚNIC lloc on es veu. Si desapareix, ja no es pot recuperar.
      if (newUser.generated_password) {
        toast.success(
          `${t('userManagement.createSuccess')} — ${t('userManagement.generatedPassword')}: ${newUser.generated_password}`,
          { autoClose: false }
        );
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('userManagement.createError'));
    }
  };

  const handleEditUser = async (userId, userData) => {
    try {
      const updatedUser = await userService.updateUser(userId, userData);
      setUsers(prev => prev.map(user => 
        user.id === userId ? updatedUser : user
      ));
      setShowEditModal(false);
      setSelectedUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('userManagement.updateError'));
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm(t('userManagement.confirmDelete'))) {
      try {
        await userService.deleteUser(userId);
        setUsers(prev => prev.filter(user => user.id !== userId));
      } catch (error) {
        toast.error(error.response?.data?.detail || t('userManagement.deleteError'));
      }
    }
  };

  const handlePasswordReset = async (userId, passwordData) => {
    try {
      await userService.changePassword(userId, passwordData);
      setShowPasswordModal(false);
      setSelectedUser(null);
      toast.success(t('userManagement.passwordChangeSuccess'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('userManagement.passwordChangeError'));
    }
  };

  const handleSendEmail = async (emailData) => {
    try {
      setSendingEmail(true);
      const result = await adminService.sendCustomEmail(emailData);
      setShowEmailModal(false);
      setSelectedUser(null);
      toast.success(t('userManagement.emailSentSuccess', { email: result.recipient.email }));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('userManagement.emailSentError'));
    } finally {
      setSendingEmail(false);
    }
  };

  const filteredUsers = users.filter(user => {
    const searchMatch = 
      user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.organization.toLowerCase().includes(searchTerm.toLowerCase());
    
    return searchMatch;
  });

  const getRoleBadge = (role) => {
    const colors = {
      administrator: 'bg-red-100 text-red-800',
      instructor: 'bg-blue-100 text-blue-800', 
      analyst: 'bg-green-100 text-green-800'
    };
    
    return (
      <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${colors[role] || 'bg-gray-100 text-gray-800'}`}>
        {t(`roles.${role}`)}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('common.never');
    return new Date(dateString).toLocaleDateString();
  };

  if (currentUser?.role !== 'administrator') {
    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900">{t('common.accessDenied')}</h2>
          <p className="text-gray-600 mt-2">{t('userManagement.noPermission')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 sm:px-0">
      {/* Header */}
      <div className="border-b border-gray-200 pb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">{t('userManagement.title')}</h1>
            <p className="text-gray-600 mt-1">{t('userManagement.subtitle')}</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-accent-600 text-white px-4 py-2 rounded-lg hover:bg-accent-700 transition-colors"
          >
            + {t('userManagement.createUser')}
          </button>
        </div>

        {/* Peticions d'alta. Va aquí dalt, i no com un filtre de la taula, perquè
            és l'única cosa d'aquesta pàgina on hi ha algú esperant: si es
            barreja amb la resta d'usuaris inactius no la mira ningú. */}
        {isAdmin && pending.length > 0 && (
          <div className="mt-6 rounded-lg border border-warning-light bg-warning-light/30 p-4">
            <div className="mb-3 flex items-center gap-2">
              <span className="inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-full bg-warning-dark px-2 text-xs font-bold text-white">
                {pending.length}
              </span>
              <h2 className="text-sm font-semibold text-neutral-900">
                {t('approvals.title')}
              </h2>
            </div>
            <p className="mb-4 text-xs leading-relaxed text-neutral-600">
              {t('approvals.description')}
            </p>

            <ul className="divide-y divide-warning-light/70">
              {pending.map((u) => (
                <li key={u.id} className="flex flex-col gap-3 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-neutral-900">
                      {u.full_name || u.username}
                    </p>
                    <p className="truncate text-xs text-neutral-600">
                      {u.username} · {u.email}
                      {u.organization ? ` · ${u.organization}` : ''}
                    </p>
                  </div>
                  <div className="flex flex-shrink-0 gap-2">
                    <button
                      onClick={() => handleApprove(u)}
                      disabled={busyId === u.id}
                      className="rounded-md bg-accent-700 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-accent-800 disabled:opacity-60"
                    >
                      {t('approvals.approve')}
                    </button>
                    <button
                      onClick={() => handleReject(u)}
                      disabled={busyId === u.id}
                      className="rounded-md border border-neutral-300 bg-white px-3 py-1.5 text-xs font-medium text-neutral-700 transition-colors hover:bg-neutral-50 disabled:opacity-60"
                    >
                      {t('approvals.reject')}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('userManagement.searchPlaceholder').split(' by')[0]}
          </label>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t('userManagement.searchPlaceholder')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-accent-500 focus:border-accent-500"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('userManagement.filterByOrganization')}
          </label>
          <select
            value={selectedOrg}
            onChange={(e) => setSelectedOrg(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-accent-500 focus:border-accent-500"
          >
            <option value="">{t('userManagement.allOrganizations')}</option>
            {organizations.map(org => (
              <option key={org} value={org}>{org}</option>
            ))}
          </select>
        </div>
        
        <div className="flex items-end">
          <button
            onClick={fetchUsers}
            disabled={loading}
            className="w-full bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            {loading ? t('common.loading') : t('common.refresh')}
          </button>
        </div>
      </div>

      {/* Users Table */}
      <div className="mt-6 bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('common.user')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('auth.organization')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('auth.role')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('common.status')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('common.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map(user => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="font-medium text-gray-900">{user.full_name || user.username}</div>
                      <div className="text-sm text-gray-500">{user.email}</div>
                      <div className="text-xs text-gray-400">@{user.username}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {user.organization}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getRoleBadge(user.role)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col space-y-1">
                      <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                        user.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        <div className={`w-2 h-2 rounded-full mr-1 ${
                          user.is_active ? 'bg-green-400' : 'bg-red-400'
                        }`}></div>
                        {user.is_active ? t('userManagement.isActive') : t('userManagement.isInactive')}
                      </span>
                      {user.must_change_password && (
                        <span className="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800" title={t('userManagement.mustChangePassword')}>
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                          {t('userManagement.passwordRequired')}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex space-x-2">
                      {/* Edit User */}
                      <button
                        onClick={() => {
                          setSelectedUser(user);
                          setShowEditModal(true);
                        }}
                        className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                        title={t('userManagement.editUser')}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      
                      {/* Reset Password */}
                      <button
                        onClick={() => {
                          setSelectedUser(user);
                          setShowPasswordModal(true);
                        }}
                        className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-lg transition-colors"
                        title={t('userManagement.resetPassword')}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </button>
                      
                      {/* Send Email */}
                      {user.email && (
                        <button
                          onClick={() => {
                            setSelectedUser(user);
                            setShowEmailModal(true);
                          }}
                          className="p-2 text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded-lg transition-colors"
                          title={t('userManagement.sendEmail')}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </button>
                      )}
                      
                      {/* Delete User */}
                      {user.id !== currentUser.id && (
                        <button
                          onClick={() => handleDeleteUser(user.id)}
                          className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                          title={t('userManagement.deleteUser')}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredUsers.length === 0 && !loading && (
          <div className="text-center py-8">
            <p className="text-gray-500">{t('userManagement.noUsersFound')}</p>
          </div>
        )}
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateUserModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateUser}
          organizations={organizations}
        />
      )}

      {showEditModal && selectedUser && (
        <EditUserModal
          user={selectedUser}
          onClose={() => {
            setShowEditModal(false);
            setSelectedUser(null);
          }}
          onSubmit={handleEditUser}
          organizations={organizations}
        />
      )}

      {showPasswordModal && selectedUser && (
        <PasswordChangeModal
          user={selectedUser}
          onClose={() => {
            setShowPasswordModal(false);
            setSelectedUser(null);
          }}
          onSubmit={handlePasswordReset}
          isAdminReset={true}
        />
      )}

      {showEmailModal && selectedUser && (
        <SendEmailModal
          user={selectedUser}
          onClose={() => {
            setShowEmailModal(false);
            setSelectedUser(null);
          }}
          onSubmit={handleSendEmail}
          isSubmitting={sendingEmail}
        />
      )}
    </div>
  );
};

export default UserManagement;